import { DownloadIcon, EditIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { Button as AriaButton, DialogTrigger } from "react-aria-components";

import { Button } from "@/components/base/button";
import { Modal, ModalHeading } from "@/components/base/modal";
import { DropZone } from "@/components/base/upload-image/dropzone";
import { Uploader } from "@/components/base/upload-image/uploader";

function RemoveImageButton({ onConfirm }: { onConfirm: () => void }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <DialogTrigger isOpen={confirmOpen} onOpenChange={setConfirmOpen}>
      <Button
        variant="icon"
        size="sm"
        className="rounded-full bg-white/80"
        tooltip="Remove Image"
      >
        <XIcon size={14} />
      </Button>
      <Modal>
        <div className="flex flex-col gap-4 p-6">
          <ModalHeading size="sm">Remove Image</ModalHeading>
          <p className="text-sm">
            Are you sure you want to remove this tournament image?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onPress={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={() => {
                onConfirm();
                setConfirmOpen(false);
              }}
            >
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </DialogTrigger>
  );
}

type TournamentImageProps = {
  imageSource: string | null;
  canEdit: boolean;
  onImageChange: (src: string | null) => void;
};

export function TournamentImage({
  imageSource,
  canEdit,
  onImageChange,
}: TournamentImageProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[] | undefined>(undefined);
  const [replaceOpen, setReplaceOpen] = useState(false);

  if (!imageSource && !canEdit) {
    return null;
  }

  return (
    <>
      {imageSource ? (
        <div className="relative">
          <AriaButton
            className="ml-auto block cursor-pointer overflow-hidden rounded-lg border border-gray-200"
            onPress={() => setLightboxOpen(true)}
          >
            <img
              src={imageSource}
              alt="Tournament"
              className="h-auto max-h-64"
            />
          </AriaButton>
          {canEdit && (
            <div className="absolute top-1 right-1 flex gap-1">
              <DialogTrigger isOpen={replaceOpen} onOpenChange={setReplaceOpen}>
                <Button
                  variant="icon"
                  size="sm"
                  className="rounded-full bg-white/80"
                  tooltip="Replace Image"
                >
                  <EditIcon size={14} />
                </Button>
                <Modal>
                  <div className="flex flex-col space-y-8 p-3">
                    <ModalHeading>Replace Tournament Image</ModalHeading>
                    <Uploader
                      bucket="tournaments"
                      prefix="tournament-images"
                      requireEdit={false}
                      onUploadSuccess={(src) => {
                        onImageChange(src);
                        setReplaceOpen(false);
                      }}
                      onCancel={() => setReplaceOpen(false)}
                    />
                  </div>
                </Modal>
              </DialogTrigger>
              <RemoveImageButton onConfirm={() => onImageChange(null)} />
            </div>
          )}
        </div>
      ) : canEdit ? (
        <>
          <DropZone onDrop={(files) => setUploadFiles(files)} />
          {uploadFiles && (
            <Modal isOpen={true} onOpenChange={() => setUploadFiles(undefined)}>
              <div className="flex flex-col space-y-8 p-3">
                <ModalHeading>Upload Tournament Image</ModalHeading>
                <Uploader
                  bucket="tournaments"
                  prefix="tournament-images"
                  requireEdit={false}
                  initialFiles={uploadFiles}
                  onUploadSuccess={(src) => {
                    onImageChange(src);
                    setUploadFiles(undefined);
                  }}
                  onCancel={() => setUploadFiles(undefined)}
                  onCancelEdit={() => setUploadFiles(undefined)}
                />
              </div>
            </Modal>
          )}
        </>
      ) : null}

      {imageSource && (
        <Modal isOpen={lightboxOpen} onOpenChange={setLightboxOpen} size="2xl">
          <div className="relative flex flex-col items-center gap-4 p-4">
            <div className="flex w-full items-center justify-between">
              <ModalHeading size="sm">Tournament Image</ModalHeading>
            </div>
            <img
              src={imageSource}
              alt="Tournament"
              className="max-h-[70vh] max-w-full object-contain"
            />
            <div className="flex w-full justify-end">
              <Button
                variant="outline"
                onPress={() => {
                  window.open(imageSource, "_blank");
                }}
              >
                <DownloadIcon size={16} />
                Download
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
